import { useCallback, useEffect, useState } from 'react';
import { Button, List, Modal, Switch, Tag, Typography } from 'antd';
import {
  createGuildInvite,
  listGuildInvites,
  revokeGuildInvite,
  type GuildInviteLink,
} from '../lib/api';

interface Props {
  guildId: string;
  onClose: () => void;
}

function inviteStatus(invite: GuildInviteLink): { label: string; color: string } {
  if (invite.revoked_at) return { label: 'Revoked', color: 'default' };
  if (invite.expires_at <= Date.now()) return { label: 'Expired', color: 'warning' };
  if (invite.consumed_uses >= invite.max_uses) return { label: 'Used up', color: 'default' };
  return { label: 'Active', color: 'success' };
}

function absoluteURL(url: string): string {
  return new URL(url, window.location.origin).toString();
}

/**
 * Owner-only invite management from the Server Members surface. The Ant Design
 * Modal portals to the document body, so it is never clipped or positioned by the
 * Members sidebar. Tokens live only in this component after creation; history
 * contains metadata only.
 */
export default function InviteLinksModal({ guildId, onClose }: Props) {
  const [invites, setInvites] = useState<GuildInviteLink[]>([]);
  const [createdURL, setCreatedURL] = useState<string | null>(null);
  const [allowAgents, setAllowAgents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoke, setRevoke] = useState<GuildInviteLink | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const nextInvites = await listGuildInvites(guildId);
      setInvites(Array.isArray(nextInvites) ? nextInvites : []);
      setError('');
    } catch {
      setError('Could not load invite links.');
    } finally {
      setLoading(false);
    }
  }, [guildId]);

  useEffect(() => {
    void load();
    const refresh = (event: Event) => {
      if ((event as CustomEvent<{ guild_id?: string }>).detail?.guild_id === guildId) void load();
    };
    window.addEventListener('guild_invites_changed', refresh);
    return () => window.removeEventListener('guild_invites_changed', refresh);
  }, [guildId, load]);

  const copy = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(absoluteURL(url));
      setError('');
      return true;
    } catch {
      setError('Could not copy the invite link. Copy it manually before closing this dialog.');
      return false;
    }
  }, []);

  async function create() {
    setCreating(true);
    try {
      const result = await createGuildInvite(guildId, { allow_owned_agents: allowAgents });
      setCreatedURL(result.url);
      await copy(result.url);
      await load();
    } catch {
      setError('Could not create invite link.');
    } finally {
      setCreating(false);
    }
  }

  async function confirmRevoke() {
    if (!revoke) return;
    setRevokingId(revoke.id);
    try {
      await revokeGuildInvite(guildId, revoke.id);
      setRevoke(null);
      await load();
    } catch {
      setError('Could not revoke invite link.');
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <Modal
      open
      centered
      title="Invite members"
      onCancel={onClose}
      width={620}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <Typography.Paragraph>
        Share a link to admit a Human to this Project. Invite URLs are never kept in this browser.
      </Typography.Paragraph>
      {error && (
        <Typography.Text type="danger" role="alert">
          {error}
        </Typography.Text>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
        <Switch
          checked={allowAgents}
          onChange={setAllowAgents}
          aria-label="Allow invited Humans to choose their active agents"
        />
        <Typography.Text>
          Let invited Humans choose their active agents after joining
        </Typography.Text>
      </div>
      <Button type="primary" onClick={() => void create()} loading={creating}>
        Create and copy invite link
      </Button>

      {createdURL && (
        <div role="status" style={{ marginTop: 16 }}>
          <Typography.Paragraph style={{ marginBottom: 4 }}>
            Invite link copied. It is shown here only until this dialog closes.
          </Typography.Paragraph>
          <Typography.Text
            copyable={{ text: absoluteURL(createdURL), tooltips: ['Copy invite link', 'Copied'] }}
          >
            {absoluteURL(createdURL)}
          </Typography.Text>
        </div>
      )}

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        Invite link history
      </Typography.Title>
      <List
        loading={loading}
        locale={{ emptyText: 'No invite links yet.' }}
        dataSource={invites}
        renderItem={(invite) => {
          const status = inviteStatus(invite);
          return (
            <List.Item
              actions={
                invite.revoked_at
                  ? undefined
                  : [
                      <Button key="revoke" danger type="link" onClick={() => setRevoke(invite)}>
                        Revoke
                      </Button>,
                    ]
              }
            >
              <List.Item.Meta
                title={
                  <>
                    <Tag color={status.color}>{status.label}</Tag>
                    <Typography.Text>
                      {invite.consumed_uses}/{invite.max_uses} uses
                    </Typography.Text>
                  </>
                }
                description={
                  <Tag>
                    {invite.allow_owned_agents
                      ? 'Agent selection allowed after joining'
                      : 'No agent selection'}
                  </Tag>
                }
              />
            </List.Item>
          );
        }}
      />
      <Modal
        open={revoke !== null}
        centered
        title="Revoke invite link"
        okText="Revoke link"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: revokingId === revoke?.id }}
        onOk={() => void confirmRevoke()}
        onCancel={() => setRevoke(null)}
      >
        <Typography.Paragraph>Revoke this invite link?</Typography.Paragraph>
        <Typography.Paragraph>
          Anyone who has not accepted it will no longer be able to join.
        </Typography.Paragraph>
      </Modal>
    </Modal>
  );
}
